import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Library, Plus, Loader2, Trash2, X, Send, BookOpen, ChevronDown, ChevronUp, Pencil, Check, Award, CheckCircle2 } from "lucide-react";

const RES_TYPE_CLS = { "Föy": "bg-sky-100 text-sky-700 border-sky-200", "Soru Bankası": "bg-amber-100 text-amber-700 border-amber-200", "Deneme": "bg-rose-100 text-rose-700 border-rose-200" };

export default function StudentLibrary({ studentId, readOnly }) {
  const [plans, setPlans] = useState([]);
  const [sections, setSections] = useState([]);
  const [lib, setLib] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [secForm, setSecForm] = useState({});
  const [editSec, setEditSec] = useState(null);

  const sf = (s) => secForm[s.id] || { correct: s.correct ?? "", wrong: s.wrong ?? "", blank: s.blank ?? "" };
  const setSf = (s, f, v) => setSecForm((p) => ({ ...p, [s.id]: { ...(secForm[s.id] || { correct: s.correct ?? "", wrong: s.wrong ?? "", blank: s.blank ?? "" }), [f]: v } }));

  const completeSection = async (s, done = true) => {
    const f = sf(s);
    const c = Number(f.correct ?? s.correct ?? 0), w = Number(f.wrong ?? s.wrong ?? 0), b = Number(f.blank ?? s.blank ?? 0);
    setBusy(true);
    try {
      const { data } = await api.put(`/resource-sections/${studentId}/${s.id}/complete`, { correct: c, wrong: w, blank: b, done });
      toast.success(done
        ? `Bölüm bitti olarak işaretlendi — Çalışma/Haftalık Programdan ${data.schedule_updated} kayıt "Onaylı" yapıldı`
        : "Bölüm 'bitti' işareti geri alındı");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSection = async (s) => {
    const f = sf(s);
    const c = Number(f.correct || 0), w = Number(f.wrong || 0), b = Number(f.blank || 0);
    setBusy(true);
    try {
      await api.put(`/resource-sections/${studentId}/${s.id}`, { ...s, correct: c, wrong: w, blank: b, total: c + w + b || Number(s.total || 0) });
      toast.success(`Bölüm kaydedildi — toplam ${c + w + b} soru olarak hesaplandı`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSectionEdit = async () => {
    const s = editSec;
    const c = Number(s.correct || 0), w = Number(s.wrong || 0), b = Number(s.blank || 0);
    setBusy(true);
    try {
      await api.put(`/resource-sections/${studentId}/${s.id}`, {
        ...s,
        total: c + w + b || Number(s.total || 0), correct: c, wrong: w, blank: b,
      });
      toast.success("Bölüm güncellendi");
      setEditSec(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get(`/resource-plans/${studentId}`),
        api.get(`/resource-sections/${studentId}`),
      ]);
      setPlans(p);
      setSections(s);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { if (studentId) load(); }, [load, studentId]);

  const openAdd = async () => {
    try {
      const { data } = await api.get("/library-resources");
      setLib(data);
      setAddOpen(true);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const assign = async (libId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/library-resources/${libId}/assign`, { target: "student", student_id: studentId, start_week: 1 });
      toast.success(`Kaynak atandı — ${data.created} bölüm oluşturuldu, Çalışma Programı'nda da görünür`);
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const delPlan = async (p) => {
    if (!window.confirm(`"${p.name}" kaynağı ve tüm bölümleri silinsin mi?`)) return;
    setBusy(true);
    try {
      await Promise.all(sections.filter((s) => s.plan_id === p.id).map((s) => api.delete(`/resource-sections/${studentId}/${s.id}`)));
      await api.delete(`/resource-plans/${studentId}/${p.id}`);
      toast.success("Kaynak silindi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const assignedLibIds = plans.map((p) => p.source_lib).filter(Boolean);
  const planStats = (p) => {
    const secs = sections.filter((s) => s.plan_id === p.id);
    const t = secs.reduce((a, s) => a + Number(s.total || 0), 0);
    const c = secs.reduce((a, s) => a + Number(s.correct || 0), 0);
    const w = secs.reduce((a, s) => a + Number(s.wrong || 0), 0);
    const b = secs.reduce((a, s) => a + Number(s.blank || 0), 0);
    const done = secs.filter((s) => s.completed).length;
    return { n: secs.length, t, c, w, b, done, net: c - w / 4, pct: secs.length ? Math.round((done / secs.length) * 100) : 0 };
  };

  return (
    <div className="space-y-5" data-testid="student-library">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Library size={20} className="text-indigo-600" /> Kaynak Kütüphanesi</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            {readOnly
              ? "Koçunuzun atadığı kaynaklar ve soru çözüm özetiniz."
              : "Öğrenciye atanan kaynaklar. 'Kaynak Ekle' ile kütüphaneden yeni kaynak atayın; satır sonundaki sil ile kaldırın."}
          </p>
        </div>
        {!readOnly && (
          <Button onClick={openAdd} className="bg-indigo-600 hover:bg-indigo-700" data-testid="mylib-add-btn">
            <Plus size={16} /><span className="ml-1">Kaynak Ekle</span>
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : plans.length === 0 ? (
        <div className="card-ta p-12 text-center text-slate-400">
          Henüz kaynak atanmamış.{!readOnly && <> <b>"Kaynak Ekle"</b> ile kütüphaneden atayın.</>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => {
            const st = planStats(p);
            return (
              <div key={p.id} className="card-ta p-4" data-testid={`mylib-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{st.n} bölüm · {st.done} tamamlandı</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${RES_TYPE_CLS[p.resource_type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.resource_type || "Genel"}</span>
                    {!readOnly && (
                      <button onClick={() => delPlan(p)} disabled={busy} className="text-slate-300 hover:text-rose-600" title="Sil" data-testid={`mylib-del-${p.id}`}><Trash2 size={15} /></button>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-1.5 text-center">
                  <div className="rounded-lg bg-slate-50 border border-slate-200 py-1.5">
                    <div className="text-[10px] font-bold uppercase text-slate-400">Toplam</div>
                    <div className="text-sm font-extrabold text-slate-800" data-testid={`mylib-total-${p.id}`}>{st.t}</div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 py-1.5">
                    <div className="text-[10px] font-bold uppercase text-emerald-500">Doğru</div>
                    <div className="text-sm font-extrabold text-emerald-700" data-testid={`mylib-correct-${p.id}`}>{st.c}</div>
                  </div>
                  <div className="rounded-lg bg-rose-50 border border-rose-200 py-1.5">
                    <div className="text-[10px] font-bold uppercase text-rose-500">Yanlış</div>
                    <div className="text-sm font-extrabold text-rose-700" data-testid={`mylib-wrong-${p.id}`}>{st.w}</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 py-1.5">
                    <div className="text-[10px] font-bold uppercase text-slate-400">Boş</div>
                    <div className="text-sm font-extrabold text-slate-600" data-testid={`mylib-blank-${p.id}`}>{st.b}</div>
                  </div>
                </div>
                <div className="mt-2.5 flex items-center gap-2">
                  <Progress value={st.pct} className="h-1.5 flex-1" />
                  <span className="text-[11px] font-mono text-slate-500">%{st.pct}</span>
                  <span className="text-[11px] font-mono font-semibold text-indigo-700">Net {st.net.toFixed(2)}</span>
                </div>
                <button onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                  className="mt-2.5 w-full flex items-center justify-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800"
                  data-testid={`mylib-expand-${p.id}`}>
                  {expandedId === p.id ? <><ChevronUp size={13} /> Bölümleri Gizle</> : <><ChevronDown size={13} /> Bölümleri Aç ({st.n})</>}
                </button>
                {expandedId === p.id && (
                  <div className="mt-2 space-y-1.5 border-t border-slate-100 pt-2" data-testid={`mylib-seclist-${p.id}`}>
                    {sections.filter((s) => s.plan_id === p.id).map((s) => {
                      const f = sf(s);
                      const autoT = Number(f.correct || 0) + Number(f.wrong || 0) + Number(f.blank || 0);
                      return (
                        <div key={s.id} className={`rounded-lg border px-2 py-1.5 ${s.completed ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid={`mylib-sec-${s.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex items-center gap-1.5">
                              {s.completed && <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />}
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-slate-800 truncate">{s.unit || "Bölüm"}</div>
                                <div className="text-[10px] text-slate-400">{s.subject}{s.pages ? ` · Föy ${s.pages}` : ""}{s.bank_pages ? ` · SB ${s.bank_pages}` : ""}</div>
                              </div>
                            </div>
                            {!readOnly && (
                              <button onClick={() => setEditSec({ ...s })} className="text-slate-300 hover:text-amber-600 shrink-0" title="Bölümü düzenle" data-testid={`mylib-sec-edit-${s.id}`}><Pencil size={13} /></button>
                            )}
                          </div>
                          {readOnly ? (
                            <div className="mt-1 text-[11px] font-mono font-bold flex items-center gap-2">
                              <span className="text-slate-600">Toplam {s.total || 0}</span>
                              <span className="text-emerald-600">D{s.correct || 0}</span>
                              <span className="text-rose-600">Y{s.wrong || 0}</span>
                              <span className="text-slate-500">B{s.blank || 0}</span>
                              <span className="text-indigo-700 ml-auto">Net {(Number(s.correct || 0) - Number(s.wrong || 0) / 4).toFixed(2)}</span>
                            </div>
                          ) : (
                            <div className="mt-1.5 flex items-center gap-1">
                              {["correct", "wrong", "blank"].map((fld) => (
                                <input key={fld} type="number" className="ta-input !px-1 !py-0.5 !text-[11px] text-center w-11"
                                  placeholder={{ correct: "D", wrong: "Y", blank: "B" }[fld]}
                                  value={f[fld]} onChange={(e) => setSf(s, fld, e.target.value)} data-testid={`mylib-sec-${fld}-${s.id}`} />
                              ))}
                              <span className="text-[11px] font-mono font-bold text-indigo-700">= {autoT} soru</span>
                              <button onClick={() => saveSection(s)} disabled={busy} className="ml-auto text-emerald-600 hover:text-emerald-800 disabled:opacity-40" title="Kaydet" data-testid={`mylib-sec-save-${s.id}`}>
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
                              </button>
                              {s.completed ? (
                                <button onClick={() => completeSection(s, false)} disabled={busy} className="text-emerald-600 hover:text-slate-500 disabled:opacity-40 flex items-center gap-0.5 text-[10px] font-bold" title="Bitti işaretini geri al" data-testid={`mylib-sec-undo-${s.id}`}>
                                  <CheckCircle2 size={14} /> Bitti
                                </button>
                              ) : (
                                <button onClick={() => completeSection(s, true)} disabled={busy} className="text-slate-400 hover:text-emerald-600 disabled:opacity-40 flex items-center gap-0.5 text-[10px] font-bold" title="Onayla & bitir (programdan kaldırılır)" data-testid={`mylib-sec-complete-${s.id}`}>
                                  <CheckCircle2 size={14} /> Bitir
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && plans.length > 0 && (() => {
        const tot = plans.reduce((a, p) => {
          const st = planStats(p);
          return { t: a.t + st.t, c: a.c + st.c, w: a.w + st.w, b: a.b + st.b, n: a.n + st.n, done: a.done + st.done };
        }, { t: 0, c: 0, w: 0, b: 0, n: 0, done: 0 });
        return (
          <div className="card-ta overflow-hidden" data-testid="mylib-report-card">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <span className="font-display font-bold text-slate-900 flex items-center gap-2"><Award size={16} className="text-amber-500" /> Kaynak Karnesi</span>
              <span className="text-xs text-slate-400">{plans.length} kaynak · {tot.n} bölüm</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500 bg-white text-xs">
                    <th className="px-4 py-2.5 font-medium">Kaynak</th>
                    <th className="px-4 py-2.5 font-medium">Tür</th>
                    <th className="px-4 py-2.5 font-medium text-right">Bölüm</th>
                    <th className="px-4 py-2.5 font-medium text-right">Toplam Soru</th>
                    <th className="px-4 py-2.5 font-medium text-right">Doğru</th>
                    <th className="px-4 py-2.5 font-medium text-right">Yanlış</th>
                    <th className="px-4 py-2.5 font-medium text-right">Boş</th>
                    <th className="px-4 py-2.5 font-medium text-right">Net</th>
                    <th className="px-4 py-2.5 font-medium text-right">Tamamlanma</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => {
                    const st = planStats(p);
                    return (
                      <tr key={p.id} className="border-b border-slate-100" data-testid={`mylib-karne-${p.id}`}>
                        <td className="px-4 py-2.5 font-semibold text-slate-800">{p.name}</td>
                        <td className="px-4 py-2.5"><span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${RES_TYPE_CLS[p.resource_type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.resource_type || "Genel"}</span></td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{st.done}/{st.n}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-slate-800">{st.t}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">{st.c}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-rose-700">{st.w}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{st.b}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-indigo-700">{st.net.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${st.pct === 100 ? "bg-emerald-100 text-emerald-700" : st.pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>%{st.pct}</span>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-indigo-50/60 font-bold" data-testid="mylib-karne-total">
                    <td className="px-4 py-2.5 text-indigo-900">GENEL TOPLAM</td>
                    <td className="px-4 py-2.5" />
                    <td className="px-4 py-2.5 text-right text-indigo-900">{tot.done}/{tot.n}</td>
                    <td className="px-4 py-2.5 text-right text-indigo-900">{tot.t}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-700">{tot.c}</td>
                    <td className="px-4 py-2.5 text-right text-rose-700">{tot.w}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{tot.b}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-indigo-900">{(tot.c - tot.w / 4).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right text-indigo-900">%{tot.n ? Math.round((tot.done / tot.n) * 100) : 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {editSec && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEditSec(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="mylib-sec-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Pencil size={17} className="text-amber-500" /> Bölümü Düzenle</h3>
              <button onClick={() => setEditSec(null)} className="text-slate-400 hover:text-slate-700" data-testid="mylib-sec-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="ta-label">Bölüm / Konu Adı</label>
                <input className="ta-input" value={editSec.unit || ""} onChange={(e) => setEditSec({ ...editSec, unit: e.target.value })} data-testid="mylib-sec-unit" />
              </div>
              <div>
                <label className="ta-label">Ders</label>
                <input className="ta-input" value={editSec.subject || ""} onChange={(e) => setEditSec({ ...editSec, subject: e.target.value })} data-testid="mylib-sec-subject" />
              </div>
              <div>
                <label className="ta-label">Sayfa (Föy / SB)</label>
                <input className="ta-input" value={editSec.pages || editSec.bank_pages || ""} onChange={(e) => setEditSec({ ...editSec, pages: e.target.value })} data-testid="mylib-sec-pages" />
              </div>
              {[["correct", "Doğru"], ["wrong", "Yanlış"], ["blank", "Boş"]].map(([f, l]) => (
                <div key={f}>
                  <label className="ta-label">{l}</label>
                  <input type="number" className="ta-input text-center" value={editSec[f] ?? ""} onChange={(e) => setEditSec({ ...editSec, [f]: e.target.value })} data-testid={`mylib-sec-m-${f}`} />
                </div>
              ))}
              <div>
                <label className="ta-label">Toplam Soru (otomatik)</label>
                <div className="ta-input text-center font-bold text-indigo-700 bg-indigo-50" data-testid="mylib-sec-autototal">
                  {Number(editSec.correct || 0) + Number(editSec.wrong || 0) + Number(editSec.blank || 0) || Number(editSec.total || 0)}
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">D/Y/B girdiğinizde toplam soru sayısı otomatik hesaplanır.</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditSec(null)}>Vazgeç</Button>
              <Button onClick={saveSectionEdit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="mylib-sec-save">
                {busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}<span className="ml-1">Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setAddOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mylib-add-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><BookOpen size={18} className="text-indigo-600" /> Kütüphaneden Kaynak Ata</h3>
              <button onClick={() => setAddOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="mylib-add-close"><X size={18} /></button>
            </div>
            {lib.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">Kütüphanede kaynak yok.</div>
            ) : (
              <div className="space-y-2">
                {lib.map((r) => {
                  const assigned = assignedLibIds.includes(r.id);
                  return (
                    <div key={r.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5" data-testid={`mylib-pick-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        {r.publisher && <div className="text-[10px] font-bold text-indigo-600 uppercase truncate">{r.publisher}</div>}
                        <div className="text-sm font-semibold text-slate-800 truncate">{r.name}</div>
                        <div className="text-[11px] text-slate-400">{r.sections} bölüm · {r.questions} soru</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold shrink-0 ${RES_TYPE_CLS[r.res_type] || RES_TYPE_CLS["Föy"]}`}>{r.res_type || "Föy"}</span>
                      <Button size="sm" disabled={busy} onClick={() => assign(r.id)}
                        className={`h-8 text-xs shrink-0 ${assigned ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-700"}`}
                        data-testid={`mylib-assign-${r.id}`}>
                        {busy ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
                        <span className="ml-1">{assigned ? "Yeniden Ata" : "Ata"}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
